import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Home as HomeIcon,
  Dashboard as DashboardIcon,
  Place as PlaceIcon,
  Assessment as AssessmentIcon,
  RateReview as RateReviewIcon,
  Article as ArticleIcon,
} from '@mui/icons-material';

const feedNavItems = [
  { label: 'Home', path: '/feed', icon: <HomeIcon /> },
  { label: 'Overview', path: '/feed/overview', icon: <DashboardIcon /> },
  { label: 'Regions', path: '/feed/regions', icon: <PlaceIcon /> },
  { label: 'Review Management', path: '/feed/reviews', icon: <RateReviewIcon /> },
  { label: 'Posting', path: '/feed/posting', icon: <ArticleIcon /> },
  { label: 'Reports', path: '/feed/reports', icon: <AssessmentIcon /> },
];

const FeedLayout = ({ children, title, subtitle, breadcrumbs }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  const currentTabIndex = feedNavItems.findIndex(item => {
    if (item.path === '/feed') {
      return currentPath === '/feed';
    }
    return currentPath.startsWith(item.path);
  });

  const handleTabChange = (event, newValue) => {
    navigate(feedNavItems[newValue].path);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0f4f8' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: '#3b5998',
              width: 48,
              height: 48,
              fontSize: '1.25rem',
              fontWeight: 'bold',
            }}
          >
            TF
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 'bold',
                color: '#1a2b4a',
                fontFamily: '"Inter", sans-serif',
              }}
            >
              The Trusted Feed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your multi-location GBP tool
            </Typography>
          </Box>
        </Box>

        {/* Navigation Tabs */}
        <Tabs
          value={currentTabIndex >= 0 ? currentTabIndex : 0}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
              minHeight: 48,
              color: '#5a6a7a',
              '&.Mui-selected': {
                color: '#3b5998',
                fontWeight: 600,
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3b5998',
              height: 3,
              borderRadius: '3px 3px 0 0',
            },
          }}
        >
          {feedNavItems.map((item) => (
            <Tab
              key={item.path}
              label={item.label}
              icon={item.icon}
              iconPosition="start"
              sx={{ gap: 1 }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Page Content */}
      <Box sx={{ p: 3 }}>
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/feed');
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon fontSize="small" />
              Home
            </Link>
            {breadcrumbs.map((crumb, index) => (
              <Link
                key={index}
                underline="hover"
                color={index === breadcrumbs.length - 1 ? 'text.primary' : 'inherit'}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (crumb.path) navigate(crumb.path);
                }}
                sx={{ cursor: crumb.path ? 'pointer' : 'default' }}
              >
                {crumb.label}
              </Link>
            ))}
          </Breadcrumbs>
        )}

        {/* Page Title */}
        {title && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                color: '#1a2b4a',
                mb: 0.5,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body1" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {/* Page Content */}
        {children}
      </Box>
    </Box>
  );
};

export default FeedLayout;
